import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Input from 'Components/Atoms/Input/Input';
import Select from 'Components/Atoms/Select/Select';
import Button from 'Components/Atoms/Button/Button';
import DatePicker from 'Components/Atoms/DatePicker/DatePicker';
import useProvidersConnections from 'Hooks/useProvidersConnections';
import { createTransaction } from 'Services/transactions';
import { useQueryClient } from '@tanstack/react-query';
import { useErrors } from 'Components/Organisms/ErrorsProvider';
import useDebounceLastValue from 'Hooks/useDebounceLastValue';
import { Trans, useTranslation } from 'react-i18next';
import { formatDateISO } from 'Utils/formatters';
import OutlineAdd from 'Components/Atoms/OutlineIcons/OutlineAdd';
import useDrawerStack from 'Hooks/useDrawerStack';
import { CATEGORY_COLORS, DRAWER_STACK } from 'Utils/constants';
import MultiselectTags from '../MultiselectTags/MultiselectTags';
import { createCategory } from 'Services/categories';
import Divider from 'Components/Atoms/Divider/Divider';

const TODAY = formatDateISO(new Date());

export default function CreateTransactionDrawer(props) {
    const {
        providerSelectedId: initialProviderSelectedId = null,
        onSubmit,
        onClose,
        defaultConcept = '',
        defaultAmount,
    } = props;

    const { open } = useDrawerStack();
    const [providerSelectedId, setProviderSelectedId] = useState(initialProviderSelectedId);

    const handleOpenAddProvider = () => {
        open(DRAWER_STACK.ADD_MANUAL_PROVIDER, {
            title: t('manual-bank.add-manual-bank'),
            onSubmit: (newProvider) => {
                setProviderSelectedId(newProvider.id);
            },
        });
    };

    const initialType = defaultAmount != null && defaultAmount < 0 ? 'spending' : 'income';
    const initialAmount = defaultAmount != null ? Math.abs(defaultAmount) : 0;

    const [provider, setProvider] = useState('');
    const [product, setProduct] = useState('');
    const [concept, setConcept] = useState(defaultConcept);
    const [type, setType] = useState(initialType);
    const [amount, setAmount] = useState(initialAmount);
    const [date, setDate] = useState(TODAY);
    const [categoriesSelected, setCategoriesSelected] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const { manualProviders } = useProvidersConnections();
    const queryClient = useQueryClient();
    const [errors, setErrors] = useState(null);
    const { showUnknownError } = useErrors();

    const { t } = useTranslation('providers');

    const TYPE_OPTIONS = [
        { value: 'income', label: t('manual-bank.income') },
        { value: 'spending', label: t('manual-bank.expense') },
    ];

    const manualProvidersOptions = useMemo(
        () =>
            manualProviders.map((item) => ({
                value: item.id,
                label: item.name,
            })),
        [manualProviders],
    );

    const productsOptions = useMemo(() => {
        const selectedProvider = manualProviders.find((item) => item.id === provider);

        if (!selectedProvider) {
            return [];
        }

        return selectedProvider.products.map((product) => ({
            value: product.id,
            label: product.name,
        }));
    }, [provider, manualProviders]);

    useEffect(() => {
        if (manualProviders.length > 0) {
            if (providerSelectedId) {
                const providerFound = manualProviders.find((item) => item.id === providerSelectedId);
                if (providerFound) {
                    setProvider(providerFound.id);
                    if (providerFound.products.length > 0) {
                        setProduct(providerFound.products[0].id);
                    }
                }
            } else {
                setProvider(manualProviders[0].id);
                const [productItem] = manualProviders[0].products;
                setProduct(productItem.id);
            }
        }
    }, [manualProviders, providerSelectedId]);

    const handleSubmit = async () => {
        let validationErrors = {};
        if (!concept) {
            validationErrors.concept = t('manual-bank.concept-required');
        }
        if (!date) {
            validationErrors.date = t('manual-bank.date-required');
        }

        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }

        setErrors(null);
        setSubmitting(true);
        try {
            const amountNormalized = Number.isNaN(Math.abs(amount)) ? 0 : Math.abs(amount);

            const body = {
                provider_connection_product_id: product,
                description: concept,
                amount: amountNormalized * (type === 'income' ? 1 : -1),
                date,
                categories_ids: categoriesSelected,
            };

            const transaction = await createTransaction(body);
            await Promise.all([
                queryClient.invalidateQueries({ queryKey: ['dashboard'] }),
                queryClient.invalidateQueries({ queryKey: ['transactions'] }),
                queryClient.invalidateQueries({ queryKey: ['providers-connections'] }),
            ]);

            onSubmit(transaction);
        } catch (error) {
            showUnknownError(error);
            onClose();
        } finally {
            setSubmitting(false);
        }
    };

    const handleChangeProvider = (value) => {
        if (value === provider) return;

        setProvider(value);
        const providerSelected = manualProviders.find((item) => item.id === value);
        if (providerSelected) {
            setProduct(providerSelected.products[0]?.id ?? null);
        } else {
            setProduct(null);
        }
    };

    const handleClickAddProvider = (ev) => {
        ev.preventDefault();
        handleOpenAddProvider();
    };

    const hasProviderSelected = !!provider;
    const hasProducts = useDebounceLastValue(productsOptions.length > 0, { initialValue: true, delay: 100 });

    const handleAddTag = () => {
        const randomColor = Math.floor(Math.random() * CATEGORY_COLORS.length);
        const color = CATEGORY_COLORS[randomColor];

        open(DRAWER_STACK.TAG_EDITOR, {
            title: t('common:create-tag'),
            tag: {
                name: '',
                color,
                icon: '💰',
            },
            onSubmit: async (data) => {
                const tag = await createCategory(data);
                await queryClient.invalidateQueries({ queryKey: ['categories'] });

                if (tag) {
                    setCategoriesSelected((prevTags) => [...prevTags, tag.id]);
                }
            },
        });
    };

    return (
        <div className="flex flex-col h-full">
            {!hasProviderSelected && (
                <div className="text-gray-500 mb-6 py-5 text-center text-pretty text-sm border border-dashed rounded border-gray-300 bg-gray-50 px-4">
                    <Trans t={t} i18nKey="manual-bank.no-products">
                        Para crear una transacción, primero debe
                        <a
                            href="#"
                            onClick={handleClickAddProvider}
                            className="text-dark-blue underline hover:no-underline"
                        >
                            crear un banco manual
                        </a>
                    </Trans>
                </div>
            )}

            <div className="flex-1 pt-3">
                <div className="grid grid-cols-2 gap-4">
                    <Select
                        label={t('manual-bank.bank')}
                        value={provider}
                        onChange={handleChangeProvider}
                        options={manualProvidersOptions}
                        className="w-full"
                        actions={[
                            <button
                                key="add-provider"
                                type="button"
                                onClick={handleOpenAddProvider}
                                className="flex items-center gap-2 w-full px-3 py-2 rounded-sm hover:bg-gray-100 transition-colors"
                            >
                                <OutlineAdd size={20} color="currentColor" />
                                <span>{t('manual-bank.add-manual-bank')}</span>
                            </button>,
                        ]}
                    />

                    <Select
                        label={t('manual-bank.account')}
                        className="w-full"
                        value={product}
                        onChange={setProduct}
                        options={productsOptions}
                        disabled={!hasProviderSelected}
                        actions={[
                            <button
                                key="add-product"
                                type="button"
                                onClick={() => {
                                    open(DRAWER_STACK.ADD_PRODUCT, {
                                        providerConnectionId: provider,
                                        title: t('add-product'),
                                        onSubmit: (newProduct) => {
                                            setProduct(newProduct.id);
                                        },
                                    });
                                }}
                                className="flex items-center gap-2 w-full px-3 py-2 rounded-sm hover:bg-gray-100 transition-colors"
                            >
                                <OutlineAdd size={20} color="currentColor" />
                                <span>{t('add-product')}</span>
                            </button>,
                        ]}
                    />

                    <div className="col-span-2">
                        <Input
                            label={t('manual-bank.concept')}
                            value={concept}
                            disabled={!hasProducts}
                            onChange={(ev) => setConcept(ev.target.value)}
                            error={errors?.concept}
                            onFocus={() => setErrors({ ...errors, concept: null })}
                            data-autofocus="true"
                        />
                    </div>

                    <div className="col-span-2">
                        <Divider className="my-3" />
                    </div>

                    <Select
                        label={t('manual-bank.type')}
                        value={type}
                        onChange={setType}
                        options={TYPE_OPTIONS}
                        disabled={!hasProducts}
                        className="w-full"
                    />

                    <Input
                        type="number"
                        label={t('manual-bank.total')}
                        min={0}
                        value={amount}
                        disabled={!hasProducts}
                        onChange={(ev) => setAmount(ev.target.value)}
                    />
                    <div className="col-span-2">
                        <DatePicker
                            label={t('manual-bank.date')}
                            value={date}
                            disabled={!hasProducts}
                            onChange={(ev) => setDate(ev.target.value)}
                            error={errors?.date}
                            onFocus={() => setErrors({ ...errors, date: null })}
                        />
                    </div>

                    <div className="col-span-2">
                        <MultiselectTags
                            label={t('manual-bank.category')}
                            disabled={!hasProducts}
                            value={categoriesSelected}
                            onChange={setCategoriesSelected}
                            onAdd={handleAddTag}
                        />
                    </div>
                </div>
            </div>

            <div className="sticky bottom-0 bg-white pt-2 pb-4 z-10">
                <div className="flex gap-2 justify-evenly">
                    <Button
                        isBusy={submitting}
                        className="grow"
                        disabled={!hasProducts}
                        variant="primary"
                        onClick={handleSubmit}
                    >
                        {t('manual-bank.add-transaction')}
                    </Button>
                    <Button onClick={onClose} className="grow">
                        {t('common:cancel')}
                    </Button>
                </div>
            </div>
        </div>
    );
}

CreateTransactionDrawer.propTypes = {
    providerSelectedId: PropTypes.string,
    onSubmit: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    defaultConcept: PropTypes.string,
    defaultAmount: PropTypes.number,
};
